import * as React from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FaUndo } from "react-icons/fa";
import {
  CalendarBlank,
  CircleHalf,
  Factory,
  Leaf,
  Lightning,
  PencilSimple,
  User,
} from "@phosphor-icons/react";
import { QUESTIONNAIRE_STATUSES, SUPPLIER_QUESTIONNAIRE_TYPES } from "../../../../utils";

// Mirrors OrganizationQuestionnaire's QuestionnaireManagePanel status bar config.
const STATUS_CONFIG = {
  [QUESTIONNAIRE_STATUSES.NEW]: { progress: 15, color: "#FC8181", label: "No data" },
  [QUESTIONNAIRE_STATUSES.IN_PROGRESS]: { progress: 50, color: "#FBD38D", label: "In Progress" },
  [QUESTIONNAIRE_STATUSES.FINALIZED]: { progress: 100, color: "#B8E086", label: "Finalised" },
};

// Mirrors SupplierTasks.jsx TYPE_COLORS so the badge reads the same in both places.
const TYPE_COLORS = {
  [SUPPLIER_QUESTIONNAIRE_TYPES.EMISSION_BASED]: { light: "#E8FAF0", text: "#0B5E30", icon: Factory, label: "Emission Based" },
  [SUPPLIER_QUESTIONNAIRE_TYPES.CONSUMPTION_BASED]: { light: "#E8F4FD", text: "#1A5276", icon: Lightning, label: "Consumption Based" },
  [SUPPLIER_QUESTIONNAIRE_TYPES.DECARBONIZATION_NET_ZERO_PLAN]: { light: "#F3E8FE", text: "#5B1D99", icon: Leaf, label: "Decarbonisation Plan" },
};

// Same icon + statusTitle label layout as OrganizationQuestionnaire's ManageInfoRow.
const ManageInfoRow = ({ icon, label, children }) => (
  <Flex gap={3} align="flex-start">
    <Flex align="center" gap={2} minW="120px" pt={0.5}>
      {icon}
      <Text fontSize="13px" fontWeight="600" color="gray.500">{label}</Text>
    </Flex>
    <Box flex={1}>{children}</Box>
  </Flex>
);

// Task Details drawer content for the supplier questionnaire, matching
// OrganizationQuestionnaire's QuestionnaireManagePanel layout (Manage tab,
// status bar, assignee/period/type rows, Edit/Unfinalise actions) scoped to
// the fields a SupplierQuestionnaireDto actually has — no dimensions, since
// suppliers don't have an organization structure.
function ManagePanel({ questionnaireData, onEditTask, onUnfinalise, isAdmin }) {
  const statusConfig = STATUS_CONFIG[questionnaireData?.status] || { progress: 0, color: "#E2E8F0", label: questionnaireData?.status };
  const typeConfig = TYPE_COLORS[questionnaireData?.questionnaireType];

  return (
    <Tabs variant="primary" defaultIndex={0} isLazy>
      <TabList position="sticky" top="0" bg="gray.50" zIndex="10" px={4}>
        <Tab>Manage</Tab>
      </TabList>
      <TabPanels>
        <TabPanel p={4}>
          <VStack align="stretch" spacing={6}>
            {/* Status */}
            <ManageInfoRow icon={<CircleHalf size={18} color="#A0AEC0" />} label="Status">
              <Box>
                <Text fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                  {statusConfig.label}
                </Text>
                <Box h="10px" borderRadius="full" bg="gray.100" overflow="hidden" w="75%">
                  <Box h="100%" w={`${statusConfig.progress}%`} borderRadius="full" bg={statusConfig.color} />
                </Box>
              </Box>
            </ManageInfoRow>

            {/* Assignee */}
            <ManageInfoRow icon={<User size={18} color="#A0AEC0" />} label="Assignee">
              <Box display="inline-block" bg="#FFF8E1" borderRadius="md" px={2} py={0.5}>
                <Text fontSize="11px" fontWeight="600" color="gray.700" textTransform="uppercase">
                  {questionnaireData?.assigneeName}
                </Text>
              </Box>
            </ManageInfoRow>

            {/* Period */}
            <ManageInfoRow icon={<CalendarBlank size={18} color="#A0AEC0" />} label="Period">
              <Text fontSize="14px" color="gray.700">
                {questionnaireData?.period?.year}
                {questionnaireData?.period?.quarter ? ` - ${questionnaireData.period.quarter}` : ""}
              </Text>
            </ManageInfoRow>

            {/* Type */}
            {typeConfig && (
              <ManageInfoRow icon={<typeConfig.icon size={18} color="#A0AEC0" />} label="Type">
                <HStack spacing={0}>
                  <Tag size="sm" bg={typeConfig.light} color={typeConfig.text} fontSize="11px" fontWeight="600" borderRadius="full" px={2} gap={1}>
                    <typeConfig.icon size={12} weight="fill" />
                    {typeConfig.label}
                  </Tag>
                </HStack>
              </ManageInfoRow>
            )}

            {/* Created */}
            {questionnaireData?.creationDate && (
              <ManageInfoRow icon={<CalendarBlank size={18} color="#A0AEC0" />} label="Created">
                <Text fontSize="14px" color="gray.700">
                  {new Date(questionnaireData.creationDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                </Text>
              </ManageInfoRow>
            )}

            {/* Edit Task */}
            {questionnaireData?.id && questionnaireData?.status !== QUESTIONNAIRE_STATUSES.FINALIZED && (
              <Button
                size="sm"
                variant="outline"
                borderColor="gray.300"
                color="gray.700"
                leftIcon={<PencilSimple size={14} />}
                onClick={onEditTask}
                mt={2}
                _hover={{ bg: "gray.50" }}
              >
                Edit Task
              </Button>
            )}

            {/* Unfinalise Task (admin + finalized only) */}
            {questionnaireData?.id && questionnaireData?.status === QUESTIONNAIRE_STATUSES.FINALIZED && isAdmin && (
              <Button
                size="sm"
                variant="outline"
                borderColor="gray.300"
                color="gray.700"
                leftIcon={<FaUndo opacity="0.7" />}
                onClick={() => onUnfinalise?.(questionnaireData.id)}
                mt={2}
                _hover={{ bg: "gray.50" }}
              >
                Unfinalise Task
              </Button>
            )}
          </VStack>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
}

export default ManagePanel;
